import React from 'react';
import { Link } from 'react-router-dom';
import { PresentationProvider } from '@context/presentation';
import { updateAnalytics, openEventStream } from '@services';
import { useQueryClient, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router';
import { salesRepReducer, initialRepState } from '@actions/reducers';
import { setPresentationId } from '@actions/creators';
import { ToastContainer } from 'react-toastify';
import { ErrorBoundary } from 'react-error-boundary';
import { getAbbreviation } from '@utils/i18n';
import { useTranslation } from 'react-i18next';
import { retrieveSavedData } from '@utils/browserStorage';
import { SidebarStyle } from './Sidebar/styles';
import { useRepClientQuery } from '@queries/client';
import { formatFirmNameForEndpoints } from '@utils/generateAssetLinks';
import { getCopyrightProfile } from '@utils/profileUtils';
import { 
    Main,
    ClientInfoDropdown,
    PresenterView, 
    Navbar, 
    PointerButton,
    Pointer,
    CurrentSlide, 
    DraggableSlideSequence, 
    ProfileCopyrightFooter, 
    SendLinkButton,
    SendSlidePdfButton,
    EndPresentationButton,
    ToggleScript,
    TogglePresentation,
    ToggleViewport,
    PresenterDocumentView,
    ClientConnectedIndicator,
    RebuttalsSection,
    IdleTimerContainer,
    ErrorFallback,
    ResetSlideSequenceButton
} from '@components';
import {  
    PAGE_URLS, 
    SSE_EVENTS, 
    PING_INTERVALS, 
    BROWSER_STORAGE_KEYS, 
    PresentationType } from '@constants';
import { 
    repOnOpenHandler, 
    errorHandler, 
    repConnectionHandler, 
    checkLocationState, 
    getPingData, 
    pingAfterDelay 
} from '@utils/eventStream';

const { useEffect, useState, useRef, useReducer } = React;
const { oneSecond, twoSeconds } = PING_INTERVALS;
const { SLIDES_PRESENTATION, DOCUMENTS_PRESENTATION, CUSTOMER_SERVICE_PRESENTATION } = PresentationType;
const { 
    clientConnectionKey, 
    viewportKey, 
    slideIdKey, 
    lastSlideIdKey, 
    presentationTypeKey, 
    pointerKey,
    presentationEndedKey } = BROWSER_STORAGE_KEYS;

export const SalesRep = ({ presentationType }) => {
    const slideRef = useRef(null);
    const queryClient = useQueryClient();
    const isSlidesPresentation = presentationType === SLIDES_PRESENTATION;
    const isCsPresentation = presentationType === CUSTOMER_SERVICE_PRESENTATION;
    const [currentlyViewing, setCurrentlyViewing] = useState(isCsPresentation ? SLIDES_PRESENTATION : presentationType);
    const [streaming, setStreaming] = useState(false);
    const [showScript, setShowScript] = useState(false);
    const [showViewportButton, setShowViewportButton] = useState(false);
    const [initialLanguageLoaded, setInitialLanguageLoaded] = useState(false);
    const [presentationState, dispatch] = useReducer(salesRepReducer, initialRepState);
    const { state: locationState } = useLocation();
    const { i18n } = useTranslation();
    const leadId = parseInt(useParams().leadId);
    const clientQueryData = {
        dispatch, 
        initialLanguageLoaded, 
        setInitialLanguageLoaded
    };

    const analyticsMutation = useMutation(analyticsData => updateAnalytics(analyticsData));
    const profile = locationState ? formatFirmNameForEndpoints(locationState.profile) : 'Strategic Consulting';
    const { isLoading, error, data: client } = useRepClientQuery({ leadId, profile }, clientQueryData);

    /* Ping connection data on an interval after the initial connection message that occurs when the event stream opens (below).
       
       If we want any data in the ping to be dynamic, we have to run the ping outside of the initial useEffect below (which intentionally 
       has no dependencies). We only want the event stream to open once when the component loads, so we need a separate useEffect to 
       capture changing values like slideId, presentationType or presentationEnded 
    */
    const { slideId, presentationEnded, language } = presentationState;

    useEffect(() => {
        i18n.changeLanguage(getAbbreviation(language));
    }, [language]);

    useEffect(() => {
        let ping;

        if (checkLocationState(locationState) && !presentationEnded) {
            const presentationData = {
                slideId, 
                presentationType: currentlyViewing, 
                repConnected: !presentationEnded,
                firstMount: false
            };

            const data = getPingData(locationState, presentationData);

            ping = pingAfterDelay(twoSeconds, data);
        }

        // Important to clear the previous interval and set a new one whenever a dependency changes
        return () => {
            clearInterval(ping);
        };
    }, [slideId, presentationEnded, currentlyViewing]);

    useEffect(() => {
        let stream;

        const presentationId = locationState ? locationState.presentationId : null;
        
        if (locationState) {
            dispatch(setPresentationId(presentationId));
        }

        // When the component first mounts (or reloads), check for & update any values saved to browser storage before load
        retrieveSavedData([slideIdKey, lastSlideIdKey, presentationTypeKey, pointerKey, presentationEndedKey, viewportKey, clientConnectionKey], dispatch);   

        // It's important that the stream is only opened once. 
        if (!streaming) {
            const { CONNECTION } = SSE_EVENTS;
            const eventStreamQueryParams = {
                connectionType: 'rep',
                presentationType,
                leadId,
                presentationId 
            };

            const connectionHandlingData = {
                dispatch, slideRef, setShowViewportButton, analyticsMutation, isSlidesPresentation, locationState
            }

            stream = openEventStream(eventStreamQueryParams);
            stream.onopen = repOnOpenHandler(locationState, { presentationType, firstMount: true }, oneSecond);
            stream.onerror = errorHandler;
            stream.addEventListener(CONNECTION, repConnectionHandler(connectionHandlingData));
            
            setStreaming(true);
        }

        /* It's important that the stream is cleared when the component dismounts (i.e. reload) to avoid
           multiple open streams piling up. stream.close() triggers a number of important cleanup events in the
           backend. See the closeConnection() function in server/api/services/sse.js for more information. */
        return () => {
            stream.close();
            setStreaming(false);
            queryClient.clear();
        };
    }, []);

    if (isLoading) return <div>Loading...</div>;
 
    if (error) return <div>An error has occurred. Please refresh the page.</div>;

    // Reps shouldn't be able to navigate to this page directly. Must enter via client-login page. 
    if (!locationState) return (
        <h3>Please enter your client&apos;s information on the <Link to={PAGE_URLS.CLIENT_LOGIN}>client login</Link> page to proceed.</h3>
    );

    const { clientConnected, viewport, slideWidth, selectedTerm, showPointer } = presentationState;

    const viewportData = { clientConnected, viewport, slideWidth };
    const viewingDocs = currentlyViewing === DOCUMENTS_PRESENTATION;
    const isDocsPresentation = presentationType === DOCUMENTS_PRESENTATION;

    return (
        <PresentationProvider
            profile={locationState.profile}
            presentationId={presentationState.presentationId}
            presentationType={presentationType}
            client={client}
            clientViewing={false}>
            <IdleTimerContainer/>
            <Navbar dispatch={dispatch} slideId={slideId} >
                {
                    !isCsPresentation && 
                        <ClientConnectedIndicator
                            clientConnected={clientConnected} />
                }
                <ClientInfoDropdown dispatch={dispatch} />
                {   !viewingDocs && 
                    <ToggleScript 
                        showScript={showScript}
                        setShowScript={setShowScript} /> }
                { !isCsPresentation && <SendLinkButton />}
                { !viewingDocs && !isCsPresentation &&
                    <SendSlidePdfButton 
                        slideId={slideId}
                        selectedTerms={selectedTerm} 
                        dispatch={dispatch}/> }
            </Navbar>
            <ToastContainer draggable={true} limit={1} />
                { isDocsPresentation && 
                    <TogglePresentation
                        currentlyViewing={currentlyViewing}
                        setCurrentlyViewing={setCurrentlyViewing} /> }
            <Main>
                <SidebarStyle>
                    {  !isCsPresentation &&  
                        <PointerButton
                            showPointer={showPointer}
                            dispatch={dispatch} /> }
                    {
                        !viewingDocs && (
                            <RebuttalsSection
                                dispatch={dispatch}
                                slideId={slideId}
                               />
                    
                        )
                    }
                    {
                        !viewingDocs && !isCsPresentation && 
                            <ResetSlideSequenceButton />
                    
                    }
                    <EndPresentationButton
                        dispatch={dispatch}
                        slideId={slideId} />
                    { showViewportButton && 
                        <ToggleViewport
                            viewportData={viewportData}
                            dispatch={dispatch} 
                            slideRef={slideRef} /> }
                </SidebarStyle>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                {
                    viewingDocs ? (
                        <PresenterDocumentView
                            slideState={presentationState}
                            dispatch={dispatch}
                            slideRef={slideRef} >
                            <Pointer
                                slideRef={slideRef}
                                dispatch={dispatch}
                                showPointer={showPointer} />
                        </PresenterDocumentView>
                    ) : (
                        <PresenterView>
                                <CurrentSlide
                                    slideState={presentationState}
                                    dispatch={dispatch}
                                    showScript={showScript}
                                    setShowScript={setShowScript}
                                    setShowViewportButton={setShowViewportButton}
                                    ref={slideRef}
                                    pdfStyleActive={false}>
                                    <Pointer
                                        dispatch={dispatch}
                                        slideRef={slideRef}
                                        showPointer={showPointer} />
                                </CurrentSlide>
                                <DraggableSlideSequence
                                    dispatch={dispatch}
                                    slideId={presentationState.slideId}/>
                        </PresenterView>
                    )
                }
                </ErrorBoundary>
            </Main>
            <ProfileCopyrightFooter profile={getCopyrightProfile(locationState.profile, client.firm)} />
        </PresentationProvider>
    );
};

export default SalesRep;
